// src/components/ChatBot.tsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AuthContext } from '../store/ContextStore';
import { useChatHistory } from '../store/ChatHistoryContext';
import ChatBotService from '../services/chatBotService';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  sources?: string[];
}

interface ChatBotProps {
  entity: string;
}

/**
 * CHATBOT COMPONENT
 *
 * How it works with backend session management:
 * 1. Backend auto-manages sessions per: username + sport_code + entity
 * 2. Sessions expire after 24 hours
 * 3. Switching sports within 24h shows old chat (backend restores context)
 * 4. Frontend just maintains UI state in React through Context (global)
 * 5. Backend automatically handles context retrieval from database
 */

const ChatBot: React.FC<ChatBotProps> = ({ entity: entityProp }) => {
  const authContext = useContext(AuthContext);
  const { team, selectedSport: contextSport, user } = authContext || {};
  
  // Use default sport if not authenticated
  const selectedSport = contextSport || 'MFB';
  const [isOpen, setIsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [entity, setEntity] = useState<string>('Player');

  // ✅ Use Context Provider for generic persistent chat management (persists across entire app)
  const { getCurrentMessages, addMessage, initializeChat } = useChatHistory();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatBotService = useRef(new ChatBotService());

  const currentMessages = getCurrentMessages();

  // ✅ DEBUG: Check message state
  useEffect(() => {
    console.log('📱 ChatBot Debug:', {
      isOpen,
      messagesCount: currentMessages.length,
      messages: currentMessages
    });
  }, [currentMessages, isOpen]);

  // ─── Sync entity on tab change ──────────────────────────────────────────────
  useEffect(() => {
    setEntity(entityProp);
  }, [entityProp]);

  // ─── Auto-scroll to latest message ─────────────────────────────────────────
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages, isOpen]);

  // ✅ SIMPLIFIED: Initialize welcome message once when chat is opened
  useEffect(() => {
    if (isOpen) {
      initializeChat();
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const question = inputText;

    // ✅ Add user message to UI immediately
    const userMessage: Message = {
      id: Date.now().toString(),
      text: question,
      sender: 'user',
      timestamp: new Date(),
    };
    addMessage(userMessage);

    setInputText('');
    setIsLoading(true);

    try {
      // ✅ SIMPLIFIED: Backend auto-manages sessions!
      // No need to send/store session_id - backend identifies session by:
      // - username (from user context)
      // - sport_code (selectedSport)
      // - entity (from parameter)
      const response = await chatBotService.current.askQuestion({
        question: question,
        top_k: 3,
        sport_code: selectedSport,
        entity: entity,
        username: user?.username || ''
        // ✅ NO session_id - backend handles it!
      });

      // ✅ Add bot response to UI
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.answer,
        sender: 'bot',
        timestamp: new Date(),
        sources: response.sources,
      };
      addMessage(botMessage);

      // ℹ️ Optional: Log session info for debugging
      console.log(`✅ Message sent | Sport: ${selectedSport} | Session: ${response.session_id}`);

    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'Sorry, I encountered an error. Please try again.',
        sender: 'bot',
        timestamp: new Date(),
      };
      addMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Chat Icon Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform z-50"
          style={{ backgroundColor: team?.colorCode || '#00B2B2' }}
        >
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
          </svg>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[500px] bg-white rounded-lg shadow-2xl flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div
            className="flex items-center justify-between p-4 rounded-t-lg"
            style={{ backgroundColor: team?.colorCode || '#00B2B2' }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <h3 className="text-white font-semibold">Ask Athlyte Bot</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {currentMessages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    message.sender === 'user'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                  style={message.sender === 'user' ? { backgroundColor: team?.colorCode || '#00B2B2' } : {}}
                >
                  {message.sender === 'bot' ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => (
                          <p className="m-0 text-sm whitespace-pre-wrap break-words">{children}</p>
                        ),
                        ul: ({ children }) => (
                          <ul className="my-0 list-disc pl-5 space-y-0.5">{children}</ul>
                        ),
                        ol: ({ children }) => (
                          <ol className="my-0 list-decimal pl-5 space-y-0.5">{children}</ol>
                        ),
                        li: ({ children }) => (
                          <li className="text-sm whitespace-pre-wrap break-words [&>p]:m-0">{children}</li>
                        ),
                        a: ({ children, href }) => (
                          <a
                            className="underline"
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {children}
                          </a>
                        ),
                        code: ({ children }) => (
                          <code className="text-xs bg-gray-200 rounded px-1 py-0.5">{children}</code>
                        ),
                      }}
                    >
                      {message.text}
                    </ReactMarkdown>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator while waiting for a bot response */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 text-sm"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isLoading}
                className="px-4 py-2 rounded-lg text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                style={{ backgroundColor: team?.colorCode || '#00B2B2' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
